import * as Yup from "yup";
import moment from "moment";

/**
 * RoadZen Create Certificate / Certificate List — values, checks and words, tested on their own (roadzenCertUtils.test.js).
 * The form uses RoadZen's own field names (proposer_name, chassis_number ...): the server checks the same rules again.
 */

export const MODE_NEW = "NEW";
export const MODE_POLICY = "POLICY";

export const FORM_FIELDS = [
  "proposer_name",
  "proposer_mobile",
  "proposer_email",
  "proposer_dob",
  "proposer_gender",
  "address_line1",
  "address_line2",
  "proposer_city",
  "proposer_state",
  "proposer_pincode",
  "nominee_name",
  "nominee_relationship",
  "nominee_gender",
  "nominee_dob",
  "nominee_is_legal_heir",
  "vehicle_make",
  "vehicle_model",
  "fuel_type",
  "registration_number",
  "chassis_number",
  "engine_number",
];
/** Needed only for a product with CPA. */
export const CPA_FIELDS = ["proposer_dob", "proposer_gender", "address_line1", "nominee_name", "nominee_relationship", "nominee_gender"];
/** Edit = RoadZen's endorsement: these never change (a different vehicle, product, price or date needs a new certificate). */
export const UPDATE_LOCKED_FIELDS = ["chassis_number", "engine_number", "registration_number", "productId", "policyPrice", "riskStartDate"];

export const initialValues = {
  ...Object.fromEntries(FORM_FIELDS.map((k) => [k, ""])),
  productId: "",
  policyPrice: "",
  riskStartDate: "",
  reason: "",
};

const MOBILE_RE = /^[6-9]\d{9}$/;

/** The form's checks; CPA fields are required only when the chosen product includes CPA. */
export const buildSchema = ({ cpaProductIds = [], isUpdate = false } = {}) => {
  const cpa = (schema, message) =>
    schema.when("productId", { is: (id) => cpaProductIds.includes(Number(id)), then: (s) => s.required(message), otherwise: (s) => s });
  return Yup.object().shape({
    proposer_name: Yup.string().trim().required("Customer Name Required").max(100, "At most 100 letters"),
    proposer_mobile: Yup.string().trim().required("Mobile Required").matches(MOBILE_RE, "Enter a 10 digit mobile starting with 6, 7, 8 or 9"),
    proposer_email: Yup.string().trim().email("Enter a valid email or leave it empty"),
    proposer_pincode: Yup.string().trim().matches(/^\d{6}$/, { message: "Enter a 6 digit pincode or leave it empty", excludeEmptyString: true }),
    proposer_dob: cpa(Yup.string(), "Date of Birth Required for a CPA product"),
    proposer_gender: cpa(Yup.string(), "Gender Required for a CPA product"),
    address_line1: cpa(Yup.string().trim(), "Address Line 1 Required for a CPA product"),
    nominee_name: cpa(Yup.string().trim(), "Nominee Name Required for a CPA product"),
    nominee_relationship: cpa(Yup.string(), "Nominee Relationship Required for a CPA product"),
    nominee_gender: cpa(Yup.string(), "Nominee Gender Required for a CPA product"),
    chassis_number: Yup.string().trim().required("Chassis Number Required"),
    productId: Yup.string().required("Product Required"),
    policyPrice: Yup.string()
      .required("Total Premium Required")
      .test("money", "Enter rupees with at most 2 decimals", (v) => !v || /^\d{1,7}(\.\d{1,2})?$/.test(String(v).trim()))
      .test("positive", "Total Premium must be more than 0", (v) => !v || Number(v) > 0)
      .test("max", "Total Premium can be ₹1,00,000 at most", (v) => !v || Number(v) <= 100000),
    riskStartDate: Yup.string().required("Risk Start Date Required"),
    reason: isUpdate ? Yup.string().trim().required("Reason Required").min(3, "At least 3 letters").max(500, "At most 500 letters") : Yup.string(),
  });
};

/** The form's values as the server wants them (only RoadZen's field names). */
export const valuesToForm = (values) => Object.fromEntries(FORM_FIELDS.map((k) => [k, String(values[k] == null ? "" : values[k]).trim()]));

/** A certificate / policy prefill into the form's values. */
export const formToValues = (form = {}, extra = {}) => ({
  ...initialValues,
  ...Object.fromEntries(FORM_FIELDS.map((k) => [k, form[k] == null ? "" : String(form[k])])),
  ...extra,
});

/** One click's fingerprint: the same details retried keep the request id (never a second certificate). */
export const attemptFingerprint = (values, mode, onlinePolicyId) =>
  [mode, onlinePolicyId || "", String(values.chassis_number || "").toUpperCase(), values.productId, values.policyPrice, values.riskStartDate].join("|");

export const policyOptionLabel = (p) => [p.policyNo, p.customerName, p.registrationNo || p.chassisNo].filter(Boolean).join(" · ");

export const productLabel = (p) => (p ? `${p.code} · ${p.name}` : "");

/** RoadZen's price is GST-inclusive 18%; Uttar Pradesh = CGST + SGST, every other state IGST (shown before RoadZen answers). */
export const splitPrice = (total, state) => {
  const paise = Math.round(Number(total) * 100);
  if (!Number.isFinite(paise) || paise <= 0) return { net: "", gst: "", kind: "" };
  const net = Math.round((paise * 100) / 118);
  return { net: (net / 100).toFixed(2), gst: ((paise - net) / 100).toFixed(2), kind: /^uttar pradesh$/i.test(String(state || "").trim()) ? "CGST + SGST" : "IGST" };
};

export const rupees = (v) =>
  v === "" || v === null || v === undefined || !Number.isFinite(Number(v))
    ? "-"
    : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const whenText = (d) => (d && moment(d).isValid() ? moment(d).format("DD-MM-YYYY hh:mm A") : "-");

/* ---------------------------------------------------------------- the list */

export const STATUS_FILTERS = [
  { _id: "all", label: "All", value: "" },
  { _id: "sold", label: "Created", value: "SOLD" },
  { _id: "draft", label: "Draft", value: "DRAFT" },
  { _id: "unknown", label: "Not known yet", value: "UNKNOWN" },
  { _id: "failed", label: "Not created", value: "FAILED" },
  { _id: "cancelled", label: "Cancelled", value: "CANCELLED" },
];

export const statusColor = (status) => {
  if (status === "SOLD") return "green";
  if (status === "CANCELLED" || status === "DRAFT") return "grey";
  if (status === "FAILED") return "red";
  return "#b54708"; // waiting for a Recheck or an admin
};

const ACTION_BUTTONS = [
  { key: "CONTINUE", label: "Continue", hint: "Continue this draft" },
  { key: "EDIT", label: "Edit", hint: "Correct the details (RoadZen re-issues it, nothing is charged)" },
  { key: "RECHECK", label: "Recheck", hint: "Ask RoadZen again (never charged twice)" },
  { key: "CANCEL", label: "Cancel", hint: "Cancel at RoadZen and refund the Company Wallet" },
  { key: "HISTORY", label: "History", hint: "What happened to this certificate" },
];
/** The row's buttons: only what the server allows, always in the same order. */
export const rowButtons = (row) => ACTION_BUTTONS.filter((b) => (row.actions || []).includes(b.key));

export const cancelRefundLine = (row) =>
  row.charge && row.charge.state === "DEBITED" && row.charge.amount
    ? `${rupees(row.charge.amount)} goes back to the Company Wallet "RoadZen" when RoadZen cancels it.`
    : "Nothing was taken from the Company Wallet for this certificate, so nothing is refunded.";

/** One history entry in plain words: a short title, its colour and picture (the full sentence stays under it). */
export const historyStep = (entry = {}) => {
  if (entry.kind === "fields") return { title: "Details changed", tone: "navy", icon: "edit" };
  if (entry.kind === "money") return { title: /refunded/i.test(entry.text || "") ? "Refunded" : /Not refunded/i.test(entry.text || "") ? "Refund held" : "Charged", tone: "navy", icon: "money" };
  if (entry.kind === "note") return /^Recheck/.test(entry.text || "") ? { title: "Recheck", tone: "navy", icon: "recheck" } : { title: "Note", tone: "grey", icon: "note" };
  const by = {
    DRAFT: { title: "Saved as a draft", tone: "grey", icon: "note" },
    SUBMITTING: { title: "Sent to RoadZen", tone: "navy", icon: "sent" },
    SOLD: { title: "Certificate created", tone: "green", icon: "done" },
    FAILED: { title: "Not created", tone: "red", icon: "failed" },
    UNKNOWN: { title: "No clear answer from RoadZen", tone: "amber", icon: "warning" },
    CANCELLED: { title: "Certificate cancelled", tone: "grey", icon: "cancel" },
    CANCEL_UNKNOWN: { title: "No clear answer to the cancel", tone: "amber", icon: "warning" },
  };
  if (entry.from === "CANCELLING" && entry.to === "SOLD") return { title: "Not cancelled", tone: "amber", icon: "warning" };
  return by[entry.to] || { title: "Update", tone: "grey", icon: "note" };
};

/** What each call to RoadZen was for, in plain words (the exact call stays in the details, for support). */
export const callLabel = (c = {}) => {
  const p = String(c.path || "");
  if (c.method === "POST" && p === "/policies") return "Asked RoadZen to create the certificate";
  if (/\/cancel$/.test(p)) return "Asked RoadZen to cancel the certificate";
  if (/\/endorsements$/.test(p)) return "Asked RoadZen to change the certificate";
  if (/\/certificate$/.test(p)) return "Asked RoadZen for the certificate PDF";
  if (/^\/policies\//.test(p)) return "Read the certificate from RoadZen";
  return `${c.method || ""} ${p}`.trim();
};

/** The chip beside each call in Details for support (same words as the Brisk list). */
export const callOutcome = (c = {}) => {
  if (c.sent === "NOT_SENT") return { label: "Not sent", ok: false };
  if (c.sent !== "ANSWERED") return { label: "No answer", ok: false };
  return c.ok ? { label: "Answered", ok: true } : { label: "Refused", ok: false };
};

/** Only an https link (our S3 copy) is ever opened. */
export const isSafeLink = (url) => /^https:\/\//i.test(String(url || ""));
